import { inject, Injectable, signal, WritableSignal } from '@angular/core';
import { catchError, filter, Observable, of, switchMap, throwError, tap } from "rxjs";
import { REFRESH_TOKEN_KEY, SafeCryptoService } from '../../api/services/safe-crypto.service';
import { Router } from '@angular/router';
import { Login, UserEntity } from '../../api/models/safe-crypto.models';
import { resolveLoginLanguage } from '../utils/language';

@Injectable({
  providedIn: 'root'
})
export class UserControllerService {
  public user!: WritableSignal<UserEntity>;
  // BE-suggested country (name, matching Country.name from /api/v1/countries),
  // derived from the request IP on login. Nullable — null when BE can't resolve.
  public suggestedCountryName: WritableSignal<string | null> = signal(null);
  private readonly router = inject(Router);
  private readonly _safeCryptoService = inject(SafeCryptoService);

  getUser(queryParamsCodes?: {referralCode?: string | null, reportedHash?: string | null}, paramsUser?: {userId: string | null, fullName: string | null, username: string | null}): Observable<UserEntity | undefined> {
    let body = this._getBodyForLogin(paramsUser);

    // if (!environment.production) {
    //   body = this._getMockBodyForLogin();
    // }

    if (!body) {
      return of(undefined);
    }
    // No logout exists: a new login is the session boundary. Drop the
    // onboarding flag here so a previous visitor on this device cannot skip
    // the preview. CaseComponent then decides from actualIncidentId and
    // getIncidents, and may set the flag again for the rest of this session.
    this._clearOnboardingSeen();
    (queryParamsCodes?.referralCode && (body.referralCode = queryParamsCodes?.referralCode));
    (queryParamsCodes?.reportedHash && (body.reportedHash = queryParamsCodes?.reportedHash));

    return this._safeCryptoService.login(body).pipe(
      tap(async (user: Login) => {
        if (!user) {
          await this.router.navigate(['/401']);
          return;
        }
        this.suggestedCountryName.set(user.suggestedCountryName ?? null);
      }),
      filter(Boolean),
      switchMap(({ userId }) => {
        return this._safeCryptoService.getUser(userId).pipe(
          tap((userData: UserEntity) => {
            this.user = signal(userData);
            if (!userData.actualGeo) {
              // this._safeCryptoService.getGeo(userData.id).pipe(take(1)).subscribe({
              //   next: ({actualGeo}: {actualGeo: string}) => {
              //     if (actualGeo) {
              //       this._safeCryptoService.putGeo(userData.id, actualGeo).pipe(take(1)).subscribe({
              //         next: () => {

              //         }
              //       });
              //     }
              //   }
              // })
            }
          }),
        )
      }
      ),
      catchError(async (error) => {
        await this.router.navigate(['/401']);
        throw error;
      })
    );
  }

  refresh(): Observable<UserEntity | undefined> {
    return this.getUser();
  }

  /**
   * Renews an expired session for the auth-session interceptor on 401.
   *
   * Presents the rotating refresh token rather than rebuilding a login body
   * from cached identity. That distinction is the point: re-asserting
   * `telegramUserId` on every expiry made each expired session another chance
   * to mint a session from client-supplied claims, so securing only the launch
   * path would have left this wide open.
   *
   * No fallback to login-from-identity — that would reopen exactly what this
   * closes. A session predating this change has no refresh token, fails here,
   * and the interceptor sends the user to /401 to relaunch from the bot; they
   * pick up a token on the next boot, since login issues one every time.
   *
   * Does not navigate; the caller decides what a failure means.
   */
  silentRelogin(): Observable<Login> {
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    if (!refreshToken) {
      return throwError(() => new Error('No refresh token available'));
    }
    return this._safeCryptoService.refreshSession(refreshToken).pipe(
      switchMap((login) => {
        if (!login?.sessionId) {
          return throwError(() => new Error('Refresh returned no sessionId'));
        }
        return of(login);
      }),
    );
  }

  /**
   * Raw `initData` for server-side HMAC verification against the bot token.
   *
   * Deliberately NOT cached alongside `telegramInfo`: the string carries an
   * `auth_date` and a hash over it, so a stale copy replayed on a later login
   * verifies as invalid. Read it fresh, and omit the field when there is none
   * (the `?userId=` browser-testing path has no Telegram context at all) —
   * absent is a case the BE expects and logs, not something to fake.
   */
  private _clearOnboardingSeen(): void {
    try {
      localStorage.removeItem('smc.onboardingSeen');
    } catch {/* private mode / quota — ignore */}
  }

  private _getInitData(): string | null {
    const initData = window.Telegram?.WebApp?.initData;
    return typeof initData === 'string' && initData.length > 0 ? initData : null;
  }

  private _getTelegramInfo() {
    const telegramInfo = window.Telegram.WebApp.initDataUnsafe;
    if (telegramInfo?.user) {
      localStorage.setItem('telegramInfo', JSON.stringify(telegramInfo));
      return telegramInfo;
    }

    const storedTelegramInfo = localStorage.getItem('telegramInfo');
    return storedTelegramInfo ? JSON.parse(storedTelegramInfo) : null;
  }

  private _getUrlInfo(paramsUser?: {userId: string | null, fullName: string | null, username: string | null}) {
    if (paramsUser && paramsUser.userId) {
      localStorage.setItem('urlInfo', JSON.stringify(paramsUser));
      return paramsUser;
    }
    const storedUrlInfo = localStorage.getItem('urlInfo');
    return storedUrlInfo ? JSON.parse(storedUrlInfo) : null;
  }

  private _getMockBodyForLogin() {
    return {
      fullName: 'Aleksandr',
      telegramUserId: '393283805',
      telegramUsername: null,
      telegramChatId: '393283805'
    }
    // ----------------------------------------
    // return {
    //   telegramUserId: '6791031723',
    //   telegramUsername: 'osckar_qa',
    //   telegramChatId: '6791031723'
    // }
    // ---------------------------------------
  }

  private _getBodyForLogin(paramsUser?: {userId: string | null, fullName: string | null, username: string | null}): any {
    const telegramData = this._getTelegramInfo();
    const urlData = this._getUrlInfo(paramsUser);

    // Login is authoritative on the BE — it overwrites user.language on every
    // call, resetting to EN if omitted. So always send the current language
    // (persisted dropdown choice, else Telegram locale; defaults to EN).
    const language = resolveLoginLanguage();

    if (telegramData && telegramData.user?.id) {
      const initData = this._getInitData();
      return {
        fullName: ((telegramData.user?.first_name || '') + (telegramData.user?.last_name || '')) || '',
        telegramUserId: telegramData.user?.id,
        telegramUsername: (telegramData.user?.username ? telegramData.user?.username : (telegramData.user?.first_name || '')) || '',
        telegramChatId: telegramData.chat?.id || telegramData.user?.id,
        language,
        // Sent verbatim — any reformatting invalidates the signature.
        ...(initData ? { initData } : {}),
      };
    }

    if (urlData && urlData?.userId) {
      return {
        fullName: ((urlData?.fullName || '') || ''),
        telegramUserId: ((urlData?.userId || '') || ''),
        telegramUsername: ((urlData?.username ? urlData?.username : (urlData?.fullName || '')) || ''),
        telegramChatId: ((urlData?.userId || '') || ''),
        language
      };
    }

    return null;
  }
}
